import { useEffect, useState } from 'react';
import { centerPerfAPI } from '../../api/client';
import { COLORS, badge } from '../../utils/uiStyles';
import { fmtKstDateTime, kstToday } from '../../utils/kstTime';
import { blobDownload } from '../../utils/meetingUtil';

/* 센터 실적 «대시보드» — BPO «센터실적» 표(전 센터 조회 대상 = /center-perf/me 의 view_all 만 이 탭이 있다).
 * · 처음에는 기준일을 비워(date='') 부르고 서버가 정한 어제(KST)로 응답의 date 를 기준일 칸에 채운다.
 *   기준일·데이터 출처(업로드 우선 / 과거 출고만)를 바꾸면 다시 읽는다. 계산·권한은 서버(/center-perf/dash/summary)가 한다.
 * · 열·순서는 응답의 groups·columns 를 그대로 그린다(엑셀도 같은 정본 — services/center_perf_dash_calc.COLUMNS).
 *   수 = 반올림 정수 + 천 단위 쉼표 · 비율(분수) = ×100 소수 1자리 % · null = '—'(분모 0 등) · blank 키 = 빈칸(수기값 미입력).
 *   달성률은 100% 이상 초록·미만 빨강 · 총계·소계는 굵게 · 첫 열은 가로 스크롤에도 붙어 있다.
 * · «엑셀 다운로드»는 화면에 그린 기준일·출처 그대로 받는다(서버 문구가 있으면 그대로 보인다).
 * · 상수 칸(최대 실적·표준 CAPA·보관 CAPA·현보관량) — 모든 사용자에게 칸 title 로 출처(sourceText)를 보인다.
 *   응답의 can_edit_constants(관리자·기준 데이터 관리 조직)면 칸마다 연필 단추 → 그 칸만 편집(현보관량은 기준일 칸도 · 기본 오늘 KST)
 *   → «저장» = dashSaveConstant(빈 값 null) → 같은 조건으로 다시 읽는다. 실패하면 서버 문구(role=alert).
 *   🔴 연필은 글자 없는 SVG 다 — 칸 textContent 가 칸 글자 그대로여야 한다(e2e 가 칸 글자를 API 값과 맞댄다).
 */
export const SOURCES = [['upload_first', '업로드 우선'], ['legacy', '과거 출고만']];
export const MISSING_TEXT = '이번 달 휴일·주차 계수 미입력 — 요일 기본값만으로 계산합니다';
export const RATE_KEYS = ['month_rate', 'year_rate'];
export const CONSTANT_KEYS = ['max_perf', 'std_capa', 'storage_capa', 'current_storage'];
const CURRENT_KEY = 'current_storage';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const inp = { padding: '6px 8px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '6px 12px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const hint = { fontSize: '12px', color: COLORS.textMuted, marginTop: '8px' };
const th = { padding: '6px 8px', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.border}`, background: COLORS.surface, textAlign: 'center' };
const td = { padding: '5px 8px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap', textAlign: 'right' };
const sticky = { position: 'sticky', left: 0, zIndex: 1, textAlign: 'left' };
const ROW_BG = { total: '#f1f3f8', subtotal: '#f8f9fb', center: COLORS.surface };
const pencil = { marginLeft: '4px', padding: '1px 3px', background: 'transparent', color: COLORS.textMuted, border: 'none', borderRadius: '4px', cursor: 'pointer', verticalAlign: 'middle', lineHeight: 0 };

// 연필 아이콘 — 글자가 없다(칸 textContent 를 바꾸지 않게).
const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M11.5 1.5l3 3L5 14H2v-3z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};

// 오류 응답이 blob 이면(엑셀 요청) 글자로 읽어 detail 을 꺼낸다 — jsdom 16 에는 Blob.text() 가 없어 FileReader 갈래.
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

async function exportErrorOf(e) {
  const data = e?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const d = JSON.parse(await readBlobText(data))?.detail;
      if (typeof d === 'string' && d) return d;
    } catch { /* 기본 문구 */ }
  }
  return detailOf(e, '엑셀을 받지 못했습니다.');
}

// 수 — 반올림 정수 + ko-KR 천 단위 쉼표. 빈 값 '—'.
export const numText = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const r = Math.round(n);
  return (r === 0 ? 0 : r).toLocaleString('ko-KR');
};

// 비율(분수) — ×100 소수 1자리 '%'. 빈 값 '—'.
export const pctText = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : '—';
};

// 칸 글자 — blank 키는 빈칸(수기값 미입력), 그 밖은 형식대로.
export function cellText(column, row) {
  if ((row.blank || []).includes(column.key)) return '';
  const v = row.values ? row.values[column.key] : null;
  return column.format === 'pct' ? pctText(v) : numText(v);
}

// 'MM/DD'(KST) — 서버 UTC 시각에서. 읽을 수 없으면 '—'.
const kstMonthDay = (at) => {
  const s = fmtKstDateTime(at);
  return s ? `${s.slice(5, 7)}/${s.slice(8, 10)}` : '—';
};

// 상수 칸 출처(title) — bpo = BPO 가져오기 날짜 · manual = 고친 사람·시각 · 소계·총계에 저장값이 없으면 구성 센터 합.
// 현보관량은 기준일을 덧붙인다.
export function sourceText(row, key) {
  const meta = (row && row.constants && row.constants[key]) || {};
  let text;
  if (row && row.kind !== 'center' && !meta.stored) text = '출처: 구성 센터 합';
  else if (meta.origin === 'bpo') text = `출처: BPO 가져오기 ${kstMonthDay(meta.at)}(KST)`;
  else if (meta.origin === 'manual') text = `출처: ${meta.by_name || '알 수 없음'} 수정 · ${fmtKstDateTime(meta.at) || '—'}`;
  else text = '출처: 기록 없음';
  if (key === CURRENT_KEY) text += ` · 기준일 ${(row && row.as_of) || '—'}`;
  return text;
}

// 편집 칸 처음 값 = 지금 값(빈칸·null 이면 '').
const editValueOf = (row, key) => {
  if ((row.blank || []).includes(key)) return '';
  const v = row.values ? row.values[key] : null;
  return v === null || v === undefined ? '' : String(v);
};

function rateColor(column, row) {
  if (!RATE_KEYS.includes(column.key) || (row.blank || []).includes(column.key)) return undefined;
  const v = row.values ? row.values[column.key] : null;
  if (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) return undefined;
  return Number(v) >= 1 ? COLORS.successText : COLORS.dangerText;
}

function fileNameOf(response, fallback) {
  const disposition = response?.headers?.['content-disposition'] || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (!encoded) return fallback;
  try { return decodeURIComponent(encoded.trim()); } catch { return fallback; }
}

export default function DashboardTab() {
  const [query, setQuery] = useState({ date: '', source: 'upload_first' });   // date '' = 서버 기본(어제 KST)
  const [dayInput, setDayInput] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exportError, setExportError] = useState('');
  const [reload, setReload] = useState(0);              // 상수를 저장한 뒤 같은 조건으로 다시 읽는다
  const [editing, setEditing] = useState(null);         // { kind, name, key, label, value, asOf }
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);         // { tone: 'ok' | 'err', text }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    centerPerfAPI.dashSummary(query.date, query.source)
      .then((r) => {
        if (!alive) return;
        const d = r.data || null;
        setData(d);
        if (!query.date && d?.date) setDayInput(d.date);      // 처음 — 서버가 정한 기준일로 칸을 채운다(다시 부르지 않는다)
      })
      .catch((e) => {
        if (!alive) return;
        setData(null);
        setError(detailOf(e, '대시보드를 불러오지 못했습니다.'));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [query, reload]);

  const startEdit = (row, column) => {
    setSaveMsg(null);
    setEditing({ kind: row.kind, name: row.name, key: column.key, label: column.label,
      value: editValueOf(row, column.key), asOf: kstToday() });
  };
  const cancelEdit = () => { if (!saving) setEditing(null); };

  const saveEdit = async () => {
    if (!editing || saving) return;
    const raw = editing.value.trim();
    const body = { kind: editing.kind, name: editing.name, field: editing.key, value: raw === '' ? null : raw };
    if (editing.key === CURRENT_KEY) body.as_of = editing.asOf || null;
    setSaving(true);
    setSaveMsg(null);
    try {
      await centerPerfAPI.dashSaveConstant(body);
      setSaveMsg({ tone: 'ok', text: `'${editing.name}' ${editing.label} 을(를) ${body.value === null ? '비웠습니다' : '저장했습니다'}.` });
      setEditing(null);
      setReload((n) => n + 1);                                 // 같은 기준일·출처로 다시 읽는다(표가 다시 계산된다)
    } catch (e) {
      setSaveMsg({ tone: 'err', text: detailOf(e, '저장하지 못했습니다.') });
    } finally {
      setSaving(false);
    }
  };

  const changeDay = (value) => {
    setDayInput(value);
    if (DATE_RE.test(value)) setQuery((q) => ({ ...q, date: value }));
  };
  const changeSource = (value) => setQuery((q) => ({ date: DATE_RE.test(dayInput) ? dayInput : q.date, source: value }));

  const download = async () => {
    if (busy || !data) return;
    setBusy(true);
    setExportError('');
    try {
      const r = await centerPerfAPI.dashExport(data.date, data.source);
      blobDownload(r.data, fileNameOf(r, `센터실적_${data.date}_${data.source_label}.xlsx`));
    } catch (e) {
      setExportError(await exportErrorOf(e));
    } finally {
      setBusy(false);
    }
  };

  const groups = data?.groups || [];
  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const head = data?.summary || {};
  const canEdit = !!data?.can_edit_constants;

  const renderCell = (c, r, strong) => {
    const constant = CONSTANT_KEYS.includes(c.key);
    const isEditing = constant && editing && editing.kind === r.kind && editing.name === r.name && editing.key === c.key;
    return (
      <td key={c.key} data-col={c.key} title={constant ? sourceText(r, c.key) : undefined}
        style={{ ...td, fontWeight: strong ? 700 : 400, color: rateColor(c, r) }}>
        {isEditing ? (
          <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
            <input aria-label={`${r.name} ${c.label} 값`} inputMode="decimal" value={editing.value} disabled={saving}
              style={{ ...inp, width: '96px', textAlign: 'right' }}
              onChange={(e) => setEditing((p) => ({ ...p, value: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
            {c.key === CURRENT_KEY && (
              <input type="date" aria-label={`${r.name} 현보관량 기준일`} value={editing.asOf} max={kstToday() || undefined}
                disabled={saving} style={inp} onChange={(e) => setEditing((p) => ({ ...p, asOf: e.target.value }))} />
            )}
            <button type="button" onClick={saveEdit} disabled={saving} style={{ ...btn(COLORS.primary), opacity: saving ? 0.5 : 1 }}>저장</button>
            <button type="button" onClick={cancelEdit} disabled={saving} style={btn(COLORS.surface, COLORS.textSub)}>취소</button>
          </span>
        ) : (
          <>
            {cellText(c, r)}
            {constant && canEdit && (
              <button type="button" aria-label={`${r.name} ${c.label} 수정`} disabled={saving}
                onClick={() => startEdit(r, c)} style={pencil}><PencilIcon /></button>
            )}
          </>
        )}
      </td>
    );
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
        <input type="date" aria-label="기준일" value={dayInput} max={kstToday() || undefined} style={inp}
          onChange={(e) => changeDay(e.target.value)} />
        <select aria-label="데이터 출처" value={query.source} style={inp} onChange={(e) => changeSource(e.target.value)}>
          {SOURCES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <button type="button" onClick={download} disabled={busy || !data}
          style={{ ...btn(COLORS.primary), opacity: busy || !data ? 0.5 : 1 }}>엑셀 다운로드</button>
        {loading && <span style={{ fontSize: '12px', color: COLORS.textMuted }}>불러오는 중…</span>}
      </div>
      {error && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
      {exportError && <div role="alert" data-testid="perf-dash-export-error" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>{exportError}</div>}
      {saveMsg && (
        <div role={saveMsg.tone === 'err' ? 'alert' : 'status'} data-testid="perf-dash-save-msg"
          style={{ color: saveMsg.tone === 'err' ? COLORS.dangerText : COLORS.successText, fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
          {saveMsg.text}
        </div>
      )}

      {data && (
        <>
          <div data-testid="perf-dash-summary" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', marginBottom: '8px' }}>
            <span data-part="week">주차 <b>{data.week}</b></span>
            <span data-part="date">기준일 <b>{data.date}</b></span>
            <span data-part="progress">진척도/월 <b>{pctText(data.progress)}</b></span>
            <span data-part="month_forecast">금월예상실적 <b>{numText(head.month_forecast)}</b></span>
            <span data-part="year_forecast">금년예상실적 <b>{numText(head.year_forecast)}</b></span>
            <span data-part="source" style={{ color: COLORS.textMuted }}>출처 {data.source_label}</span>
          </div>
          {data.progress_missing && (
            <div role="status" data-testid="perf-dash-missing"
              style={{ ...badge('warn'), display: 'block', padding: '6px 10px', borderRadius: '6px', fontSize: '13px', marginBottom: '8px' }}>
              {MISSING_TEXT}
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="perf-dash-table" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ ...th, ...sticky, zIndex: 2 }}>센터</th>
                  {groups.map((g) => (
                    <th key={g.key} colSpan={g.span} data-group={g.key} style={{ ...th, fontWeight: 700, color: COLORS.text }}>{g.label}</th>
                  ))}
                </tr>
                <tr>
                  {columns.map((c) => <th key={c.key} data-col={c.key} style={th}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const strong = r.kind !== 'center';
                  const bg = ROW_BG[r.kind] || COLORS.surface;
                  return (
                    <tr key={`${r.kind}:${r.name}`} data-row={r.name} data-kind={r.kind} style={{ background: bg }}>
                      <td style={{ ...td, ...sticky, background: bg, fontWeight: strong ? 700 : 400 }}>{r.name}</td>
                      {columns.map((c) => renderCell(c, r, strong))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={hint}>
            총계 = 사용 중 실적 센터 합(소계는 다시 더하지 않음) · 소계 = 구성 센터 합 · 사용 안 함 센터는 표에 없습니다.
            기간 목표 = 월간 목표 × 진척도(날짜별 가중치) · 요일 평균 = 최근 4주 그 요일 합 ÷ 4.
            &apos;—&apos; = 분모가 0 · 빈칸 = 기준 데이터 수기값(최대실적·표준 CAPA·보관 CAPA·현보관량) 미입력.
            소계·총계의 최대실적·표준 CAPA·보관 CAPA·현보관량은 저장값이 있으면 그 값, 없으면 구성 센터 합입니다.
            보관률 = 현보관량 ÷ 보관 CAPA(보관 CAPA 가 비거나 0 이면 &apos;—&apos;). 칸에 마우스를 올리면 값의 출처가 보입니다.
          </div>
        </>
      )}
    </div>
  );
}
